// components/CaptchaWidget.tsx
import { useEffect, useRef, useCallback } from "react";

const CHARS = "45ABCDEFGHJKLMNPQRSTUVWXYZ4512abcdefghjkmnpqrstuvwxyz23456789";

const generateCode = (len = 6) =>
  Array.from(
    { length: len },
    () => CHARS[Math.floor(Math.random() * CHARS.length)],
  ).join("");

interface Props {
  value: string; // current captcha code
  onChange: (code: string) => void; // called when refreshed
  disabled?: boolean;
}

const CaptchaWidget = ({ value, onChange, disabled = false }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const draw = useCallback((code: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const W = canvas.width,
      H = canvas.height;

    // background
    ctx.fillStyle = "#f0f4ff";
    ctx.fillRect(0, 0, W, H);

    // noise lines
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = `hsl(${Math.random() * 360},50%,70%)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(Math.random() * W, Math.random() * H);
      ctx.lineTo(Math.random() * W, Math.random() * H);
      ctx.stroke();
    }

    // noise dots
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `hsl(${Math.random() * 360},50%,60%)`;
      ctx.beginPath();
      ctx.arc(Math.random() * W, Math.random() * H, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // characters
    code.split("").forEach((ch, i) => {
      ctx.font = `bold ${22 + Math.random() * 6}px monospace`;
      ctx.fillStyle = `hsl(${Math.random() * 360},60%,30%)`;
      ctx.save();
      ctx.translate(18 + i * 28, H / 2 + 6);
      ctx.rotate((Math.random() - 0.5) * 0.5);
      ctx.fillText(ch, 0, 0);
      ctx.restore();
    });
  }, []);

  const refresh = () => {
    const code = generateCode();
    onChange(code);
    draw(code);
  };

  useEffect(() => {
    if (value) draw(value);
  }, [value, draw]);

  useEffect(() => {
    refresh(); // generate on mount
  }, []); // eslint-disable-line

  return (
    <div className="d-flex align-items-center gap-2 mb-2 pb-1">
      <canvas
        ref={canvasRef}
        width={180}
        height={40}
        className="border rounded"
        style={{ cursor: "pointer" }}
        title="Click to refresh"
      // onClick={refresh}
      />
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={refresh}
        disabled={disabled}
      >
        <i className="ti ti-refresh" />
      </button>
    </div>
  );
};

export default CaptchaWidget;
